import { useMemo, useState } from 'react';

import { useI18n } from '../i18n/LanguageProvider.js';
import type { TranslationKey } from '../i18n/translations.js';
import {
  buildResponsePath,
  pathPassesThrough,
  pathReaches,
  type PathSegment,
} from '../lib/responsePath.js';

/**
 * The provider's reply as a tree to point at. Writing a response path by hand
 * means knowing exactly how the reply is shaped; here the shape is on screen,
 * a tap on the value that matters writes the path in the platform's syntax,
 * and the places the saved paths already land are marked.
 */

/** What a tapped node can be used as. `usage` targets are added by the caller when the connector has them. */
export type PathTargetId = 'result' | 'operation' | 'done' | 'mime' | (string & {});

export interface ExtraPathTarget {
  id: string;
  label: string;
}

export interface PathsInEffect {
  result?: string | null;
  operation?: string | null;
  done?: string | null;
  mime?: string | null;
  [other: string]: string | null | undefined;
}

interface Props {
  /** The stored reply text (pretty JSON when the reply was JSON). */
  raw: string;
  /** The paths now in the form, so their landing places are marked. */
  paths: PathsInEffect;
  /** Called with the target and the path; absent where there is no form to write into. */
  onUse?: (target: PathTargetId, path: string) => void;
  /** More things a node can be used as (for example usage counters), each shown only when given. */
  extraTargets?: ExtraPathTarget[];
}

const PREVIEW_CHARS = 60;

const MARKS: Array<{ id: 'result' | 'operation' | 'done' | 'mime'; label: TranslationKey }> = [
  { id: 'result', label: 'studioConnectors.tree.mark.result' },
  { id: 'operation', label: 'studioConnectors.tree.mark.operation' },
  { id: 'done', label: 'studioConnectors.tree.mark.done' },
  { id: 'mime', label: 'studioConnectors.tree.mark.mime' },
];

function parseReply(raw: string): { ok: true; value: unknown } | { ok: false } {
  try {
    const value: unknown = JSON.parse(raw);
    return typeof value === 'object' && value !== null ? { ok: true, value } : { ok: false };
  } catch {
    return { ok: false };
  }
}

const isContainer = (value: unknown): value is Record<string, unknown> | unknown[] =>
  typeof value === 'object' && value !== null;

interface NodeProps {
  label: string;
  segments: PathSegment[];
  value: unknown;
  depth: number;
  paths: PathsInEffect;
  selected: PathSegment[] | null;
  onSelect: (segments: PathSegment[]) => void;
}

function sameSegments(a: PathSegment[] | null, b: PathSegment[]): boolean {
  return a !== null && a.length === b.length && a.every((segment, index) => segment === b[index]);
}

function TreeNode({ label, segments, value, depth, paths, selected, onSelect }: NodeProps) {
  const { t } = useI18n();
  const container = isContainer(value);
  const marks = MARKS.filter((mark) => pathReaches(segments, paths[mark.id]));
  const onTheWay = MARKS.some((mark) => pathPassesThrough(segments, paths[mark.id]));
  const [open, setOpen] = useState(depth < 2 || onTheWay);

  const entries: Array<[string | number, unknown]> = Array.isArray(value)
    ? value.map((entry, index) => [index, entry])
    : container
      ? Object.entries(value)
      : [];

  const preview = container
    ? Array.isArray(value)
      ? t('studioConnectors.tree.items', { n: value.length })
      : t('studioConnectors.tree.fields', { n: entries.length })
    : typeof value === 'string'
      ? value.length > PREVIEW_CHARS
        ? `"${value.slice(0, PREVIEW_CHARS)}…"`
        : `"${value}"`
      : String(value);

  const isSelected = sameSegments(selected, segments);

  return (
    <li role="treeitem" aria-expanded={container ? open : undefined} aria-selected={isSelected}>
      <div className="tree-row">
        {container ? (
          <button
            type="button"
            className="tree-toggle"
            aria-label={open ? t('studioConnectors.tree.collapse') : t('studioConnectors.tree.expand')}
            onClick={() => setOpen(!open)}
          >
            {open ? '▾' : '▸'}
          </button>
        ) : (
          <span className="tree-toggle" aria-hidden="true" />
        )}
        <button
          type="button"
          className={`tree-node${isSelected ? ' selected' : ''}${marks.length > 0 ? ' hit' : ''}`}
          data-hit={marks.length > 0 ? marks.map((mark) => mark.id).join(' ') : undefined}
          title={typeof value === 'string' ? value : undefined}
          onClick={() => onSelect(segments)}
        >
          <span className="tree-key mono">{label}</span>
          <span className={`tree-value mono${container ? ' muted' : ''}`}>{preview}</span>
          {marks.map((mark) => (
            <span key={mark.id} className="badge ok tiny">
              {t(mark.label)}
            </span>
          ))}
        </button>
      </div>
      {container && open && entries.length > 0 && (
        <ul role="group" className="tree-children">
          {entries.map(([key, child]) => (
            <TreeNode
              key={key}
              label={typeof key === 'number' ? `[${key}]` : key}
              segments={[...segments, key]}
              value={child}
              depth={depth + 1}
              paths={paths}
              selected={selected}
              onSelect={onSelect}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

const TARGETS: Array<{ id: 'result' | 'operation' | 'done' | 'mime'; label: TranslationKey }> = [
  { id: 'result', label: 'studioConnectors.tree.use.result' },
  { id: 'operation', label: 'studioConnectors.tree.use.operation' },
  { id: 'done', label: 'studioConnectors.tree.use.done' },
  { id: 'mime', label: 'studioConnectors.tree.use.mime' },
];

export function ProviderResponseTree({ raw, paths, onUse, extraTargets = [] }: Props) {
  const { t } = useI18n();
  const parsed = useMemo(() => parseReply(raw), [raw]);
  const [selected, setSelected] = useState<PathSegment[] | null>(null);
  const [anyItem, setAnyItem] = useState(false);
  const [used, setUsed] = useState<string | null>(null);

  if (!parsed.ok) return <p className="muted small">{t('studioConnectors.tree.notJson')}</p>;

  const path = selected ? buildResponsePath(selected, anyItem) : null;
  const hasPosition = selected?.some((segment) => typeof segment === 'number') ?? false;

  const use = (target: PathTargetId) => {
    if (!path || !onUse) return;
    onUse(target, path);
    setUsed(t('studioConnectors.tree.used', { path }));
  };

  const rootEntries: Array<[string | number, unknown]> = Array.isArray(parsed.value)
    ? parsed.value.map((entry, index) => [index, entry])
    : Object.entries(parsed.value as Record<string, unknown>);

  return (
    <div className="response-tree">
      <p className="muted tiny">{t('studioConnectors.tree.hint')}</p>

      <label className="check-row">
        <input type="checkbox" checked={anyItem} onChange={(event) => setAnyItem(event.target.checked)} />
        <span className="tiny">{t('studioConnectors.tree.anyItem')}</span>
      </label>

      <ul role="tree" className="tree-root" dir="ltr">
        {rootEntries.map(([key, child]) => (
          <TreeNode
            key={key}
            label={typeof key === 'number' ? `[${key}]` : key}
            segments={[key]}
            value={child}
            depth={0}
            paths={paths}
            selected={selected}
            onSelect={(segments) => {
              setSelected(segments);
              setUsed(null);
            }}
          />
        ))}
      </ul>

      {selected && (
        <div className="tree-picked" role="status">
          {path ? (
            <>
              <p className="small">
                {t('studioConnectors.tree.selected')} <code className="ltr mono">{path}</code>
              </p>
              {anyItem && !hasPosition && <p className="muted tiny">{t('studioConnectors.tree.noPosition')}</p>}
              {onUse ? (
                <div className="tree-use">
                  <span className="tiny">{t('studioConnectors.tree.useAs')}</span>
                  {TARGETS.map((target) => (
                    <button
                      key={target.id}
                      type="button"
                      className="chip-toggle"
                      onClick={() => use(target.id)}
                    >
                      {t(target.label)}
                    </button>
                  ))}
                  {extraTargets.map((target) => (
                    <button key={target.id} type="button" className="chip-toggle" onClick={() => use(target.id)}>
                      {target.label}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="muted tiny">{t('studioConnectors.tree.readOnly')}</p>
              )}
              {used && <p className="ok-text tiny">{used}</p>}
            </>
          ) : (
            <p className="warn-text small">{t('studioConnectors.tree.cannotAddress')}</p>
          )}
        </div>
      )}
    </div>
  );
}
